import React, { useState } from "react";
import { Modal, Button, Form, Spinner, Badge, Alert } from "react-bootstrap";
import axios from "axios";
import toast from "react-hot-toast";

const HITYPE_OPTIONS = [
  { value: "OPConsultation", label: "OP Consultation" },
  { value: "Prescription", label: "Prescription" },
  { value: "DiagnosticReport", label: "Diagnostic Report" },
  { value: "DischargeSummary", label: "Discharge Summary" },
  { value: "ImmunizationRecord", label: "Immunization Record" },
  { value: "WellnessRecord", label: "Wellness Record" },
  { value: "HealthDocumentRecord", label: "Health Document Record" },
];

function ConsentRequestDialog({ show, onHide, patientId, patientAbha, onComplete }) {
  const [selectedHiTypes, setSelectedHiTypes] = useState([
    "OPConsultation",
    "Prescription",
    "DiagnosticReport",
  ]);
  const [purpose, setPurpose] = useState("CAREMGT");
  const [fromTime, setFromTime] = useState(
    new Date(Date.now() - 365 * 24 * 3600 * 1000).toISOString().split("T")[0]
  );
  const [toTime, setToTime] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleToggleHiType = (ht) => {
    setSelectedHiTypes((prev) =>
      prev.includes(ht) ? prev.filter((x) => x !== ht) : [...prev, ht]
    );
  };

  const handleSubmit = async () => {
    if (selectedHiTypes.length === 0) {
      toast.error("Select at least one health information type.");
      return;
    }

    setSubmitting(true);
    setResult(null);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/abdm/admin/hiu/request-data/${patientId}`,
        {
          hiTypes: selectedHiTypes,
          purpose,
          fromTime: new Date(fromTime).toISOString(),
          toTime: new Date(toTime).toISOString(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(data);
      toast.success("Consent request sent. Patient will get notified on their ABHA app.");
      onComplete?.(data);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Request failed";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
      setResult({ error: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setSelectedHiTypes(["OPConsultation", "Prescription", "DiagnosticReport"]);
    setFromTime(new Date(Date.now() - 365 * 24 * 3600 * 1000).toISOString().split("T")[0]);
    setToTime(new Date().toISOString().split("T")[0]);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Pull Patient Health History</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!result ? (
          <>
            <Alert variant="info">
              Request health records for this patient from ABDM network hospitals.
              The patient will receive a notification on their ABHA app to approve this request.
            </Alert>
            <Form.Group className="mb-3">
              <Form.Label>ABHA Address</Form.Label>
              <Form.Control type="text" value={patientAbha || ""} disabled />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Purpose</Form.Label>
              <Form.Select value={purpose} onChange={(e) => setPurpose(e.target.value)}>
                <option value="CAREMGT">Care Management</option>
                <option value="CLINICAL">Clinical Care</option>
                <option value="PUBLHEALTH">Public Health</option>
                <option value="HRESEARCH">Health Research</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Health Information Types</Form.Label>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {HITYPE_OPTIONS.map((ht) => (
                  <Badge
                    key={ht.value}
                    bg={selectedHiTypes.includes(ht.value) ? "primary" : "secondary"}
                    style={{ cursor: "pointer", padding: "6px 12px" }}
                    onClick={() => handleToggleHiType(ht.value)}
                  >
                    {ht.label}
                  </Badge>
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Date Range</Form.Label>
              <div className="d-flex gap-3">
                <Form.Control
                  type="date"
                  value={fromTime}
                  onChange={(e) => setFromTime(e.target.value)}
                />
                <span className="align-self-center">to</span>
                <Form.Control
                  type="date"
                  value={toTime}
                  onChange={(e) => setToTime(e.target.value)}
                />
              </div>
            </Form.Group>
          </>
        ) : (
          <>
            <Alert variant={result.error ? "danger" : "success"}>
              {result.error || "Consent request initiated successfully!"}
            </Alert>
            {result.consentRequestId && (
              <div className="mb-2">
                <strong>Consent Request ID:</strong>{" "}
                <code>{result.consentRequestId}</code>
              </div>
            )}
            {result.status && (
              <div className="mb-2">
                <strong>Status:</strong>{" "}
                <Badge bg="warning">{result.status}</Badge>
              </div>
            )}
            <p className="text-muted small mt-2">
              The patient needs to approve this request via their ABHA app.
              Once approved, the system will automatically fetch and decrypt the records.
              You can check the status in the ABDM HIU Dashboard.
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result ? (
          <>
            <Button variant="secondary" onClick={handleReset}>
              New Request
            </Button>
            <Button variant="primary" onClick={onHide}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || selectedHiTypes.length === 0 || !patientAbha}
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sending Request…
                </>
              ) : (
                "Request Patient History"
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ConsentRequestDialog;
